import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { logoProfilesApi, seriesApi, programCategoriesApi, programGenresApi, type SubGenreSuggestion } from '../../lib/api.js'
import type { Series, ProgramOrigin, LogoProfile, ProgramCategory, ProgramGenre } from '../../types/domain.js'
import { PROGRAM_ORIGIN_LABELS } from '../../types/domain.js'
import { defaultLogoProfileId } from '../../lib/utils.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface SeriesModalProps {
  channelId:  string
  series?:    Series
  onCreated?: (s: Series) => void
  onUpdated?: (s: Series) => void
  onClose:    () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const SeriesModal = memo(function SeriesModal({ channelId, series, onCreated, onUpdated, onClose }: SeriesModalProps) {
  const isEdit = !!series
  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const [title,        setTitle]        = useState(series?.title        ?? '')
  const [titleShort, setTitleShort] = useState(series?.titleShort ?? '')
  const [description,  setDescription]  = useState(series?.description  ?? '')
  const [programCode, setProgramCode] = useState(series?.programCode ?? '')
  const [programCategoryId, setProgramCategoryId] = useState(series?.programCategoryId ?? '')
  const [categories, setCategories] = useState<ProgramCategory[]>([])
  // Genre publié à l'EPG (liste fermée) et sa précision libre : recopiés dans
  // chaque nouvelle édition. L'ancien `genre` texte libre n'est plus saisi.
  const [programGenreId, setProgramGenreId] = useState(series?.programGenreId ?? '')
  const [subGenre,       setSubGenre]       = useState(series?.subGenre ?? '')
  const [genres,         setGenres]         = useState<ProgramGenre[]>([])
  const [subGenreOptions, setSubGenreOptions] = useState<SubGenreSuggestion[]>([])
  const [applyGenreToUnclassified, setApplyGenreToUnclassified] = useState(true)
  const [tags, setTags] = useState((series?.tags ?? []).join(', '))
  const [director, setDirector] = useState(series?.director ?? '')
  const [presenter, setPresenter] = useState(series?.presenter ?? '')
  const [production, setProduction] = useState(series?.production ?? '')
  const [logoProfileId, setLogoProfileId] = useState(series?.logoProfileId ?? '')
  const [isFourThree, setIsFourThree] = useState(series?.isFourThree ?? false)
  const [partDurationsMin, setPartDurationsMin] = useState(
    series?.defaultPartDurationsSec?.length ? series.defaultPartDurationsSec.map(value => String(value / 60)) : ['52'],
  )
  const [interPartBreakMin, setInterPartBreakMin] = useState(String((series?.defaultInterPartBreakSec ?? 0) / 60))
  // Série livrée en deux versions (longue / courte) : la génération pose la
  // courte sur les créneaux courts, même avant la livraison de son fichier.
  const [hasShortVersion, setHasShortVersion] = useState(Boolean(series?.shortVersionDurationSec))
  const [shortVersionMin, setShortVersionMin] = useState(series?.shortVersionDurationSec ? String(series.shortVersionDurationSec / 60) : '13')
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])
  const [totalSeasons, setTotalSeasons] = useState(String(series?.totalSeasons ?? ''))
  // Toutes les émissions ne se numérotent pas : celles qu'un producteur livre
  // par lots n'ont ni saison ni épisode. Le choix se révise à tout moment.
  const [numberEpisodes, setNumberEpisodes] = useState(series?.numberEpisodes ?? true)
  // Tri des épisodes dans la fiche série : chronologique par défaut, plus
  // récent d'abord si activé.
  const [episodeSortDescending, setEpisodeSortDescending] = useState(series?.episodeSortDescending ?? false)
  const [origin,       setOrigin]       = useState<ProgramOrigin | ''>(series?.origin ?? '')
  // Valeurs EPG de la série : une émission hebdomadaire garde sa rubrique
  // partenaire d'une semaine sur l'autre, et la ressaisir sur chaque édition
  // serait la première chose oubliée. `epgShowId` est l'`idemission` de Media
  // Press, commun à toutes les éditions : il se saisit ici et nulle part ailleurs.
  const [epgFederation, setEpgFederation] = useState(series?.epgFederation ?? '')
  const [epgThemeId,    setEpgThemeId]    = useState(series?.epgThemeId != null ? String(series.epgThemeId) : '')
  const [epgShowId,     setEpgShowId]     = useState(series?.epgShowId  != null ? String(series.epgShowId)  : '')

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  useEffect(() => {
    logoProfilesApi.list(channelId).then(profiles => {
      setLogoProfiles(profiles)
      setLogoProfileId(current => profiles.some(profile => profile.active && profile.id === current)
        ? current
        : defaultLogoProfileId(profiles))
    }).catch(console.error)
    programCategoriesApi.list({ channelId }).then(setCategories).catch(console.error)
    programGenresApi.list({ channelId }).then(setGenres).catch(console.error)
    programGenresApi.subGenres({ channelId }).then(setSubGenreOptions).catch(console.error)
  }, [channelId])

  // Éditions déjà créées sans genre : ce sont elles que le rattrapage vise.
  // Les parties suivent leur racine côté serveur, seules les racines comptent.
  const unclassifiedCount = useMemo(
    () => (series?.programs ?? []).filter(episode => episode.programGenreId === null).length,
    [series?.programs],
  )
  // Un genre désactivé depuis reste porté par la série : il doit rester
  // visible dans la liste, sinon le sélecteur afficherait « Aucun genre » et
  // l'enregistrement l'effacerait sans que l'opérateur l'ait demandé.
  const genreOptions = useMemo(() => {
    const current = series?.programGenre
    if (current && !genres.some(g => g.id === current.id)) {
      return [...genres, { ...current, label: `${current.label} (désactivé)` } as ProgramGenre]
    }
    return genres
  }, [genres, series?.programGenre])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !logoProfileId) return
    // Abandonner la numérotation efface les numéros déjà posés : l'opérateur
    // doit le savoir avant d'enregistrer, l'opération n'est pas réversible.
    if (isEdit && series?.numberEpisodes && !numberEpisodes) {
      const numbered = (series.programs ?? []).filter(episode => episode.season != null || episode.episode != null).length
      if (!confirm(
        `Passer « ${series.title} » sans saison ni épisode ?\n\n` +
        (numbered > 0
          ? `Les numéros de ${numbered} édition${numbered > 1 ? 's' : ''} seront effacés définitivement.`
          : 'Les éditions de cette série ne seront plus numérotées.'),
      )) return
    }
    setSaving(true); setError(null)
    const data = {
      title:        title.trim(),
      titleShort: titleShort.trim() || undefined,
      description:  description.trim() || undefined,
      programCode: programCode.trim() ? programCode.trim().toUpperCase() : null,
      type:         'EMISSION' as const,
      programCategoryId: programCategoryId || null,
      programGenreId: programGenreId || null,
      // `null` et non `undefined` : vider le champ doit retirer la précision.
      subGenre:       subGenre.trim() || null,
      tags: tags.split(',').map(value => value.trim()).filter(Boolean),
      director: director.trim() || undefined,
      presenter: presenter.trim() || undefined,
      production: production.trim() || undefined,
      logoProfileId,
      isFourThree,
      defaultPartDurationsSec: partDurationsMin.map(value => Math.max(1, Math.round(Number(value || 1) * 60))),
      defaultInterPartBreakSec: Math.max(0, Math.round(Number(interPartBreakMin || 0) * 60)),
      shortVersionDurationSec: hasShortVersion && partDurationsMin.length === 1
        ? Math.max(1, Math.round(Number(shortVersionMin || 0) * 60))
        : null,
      numberEpisodes,
      episodeSortDescending,
      totalSeasons: numberEpisodes && totalSeasons ? parseInt(totalSeasons) : null,
      origin:       (origin as ProgramOrigin) || undefined,
      // `null` et non `undefined` : vider un champ EPG doit le retirer du flux.
      epgFederation: epgFederation.trim() || null,
      epgThemeId:    epgThemeId.trim() ? parseInt(epgThemeId, 10) : null,
      epgShowId:     epgShowId.trim()  ? parseInt(epgShowId, 10)  : null,
    }
    try {
      if (isEdit && series) {
        const updated = await seriesApi.update(series.id, {
          ...data,
          applyGenreToUnclassified: unclassifiedCount > 0 && !!programGenreId && applyGenreToUnclassified,
        })
        onUpdated?.(updated)
      } else {
        const created = await seriesApi.create({ channelId, ...data })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message); setSaving(false)
    }
  }, [channelId, isEdit, series, title, titleShort, description, programCode, programCategoryId, programGenreId, subGenre, unclassifiedCount, applyGenreToUnclassified, tags, director, presenter, production, logoProfileId, isFourThree, partDurationsMin, interPartBreakMin, hasShortVersion, shortVersionMin, totalSeasons, numberEpisodes, episodeSortDescending, origin, epgFederation, epgThemeId, epgShowId, onCreated, onUpdated])

  return (
    <ModalShell
      title={isEdit ? 'Modifier la série' : 'Nouvelle série'}
      size="wide"
      visible={visible}
      onClose={handleClose}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="series-title">Titre de la série *</label>
            <input id="series-title" value={title} onChange={e => setTitle(e.target.value)} required autoFocus className="tp-input" placeholder="Ex: Les routes de France" />
          </div>

          <div className="tp-form-grid tp-form-grid--three">
            <SeriesField label="Titre court">
              <input value={titleShort} onChange={e => setTitleShort(e.target.value)} maxLength={60} className="tp-input" />
            </SeriesField>
            <SeriesField label="Code programme">
              <input value={programCode} onChange={e => setProgramCode(e.target.value.toUpperCase())} className="tp-input tp-input--data" placeholder="EX: JTLOCAL" />
            </SeriesField>
            {/* Même libellé que la fiche programme : le format est interne,
                le genre (plus bas) est ce qui part à l'EPG. */}
            <SeriesField label="Format">
              <select value={programCategoryId} onChange={e => setProgramCategoryId(e.target.value)} className="tp-select">
                <option value="">— Aucun format —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </SeriesField>
          </div>

          <div className="tp-form-grid catalog-series-origin-grid">
            <div className="tp-form-field">
              <label className="tp-field-label" htmlFor="series-origin">Origine</label>
              <select id="series-origin" value={origin} onChange={e => setOrigin(e.target.value as ProgramOrigin | '')} className="tp-select">
                <option value="">— Non précisée —</option>
                {(Object.entries(PROGRAM_ORIGIN_LABELS) as [ProgramOrigin, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            {numberEpisodes && (
              <div className="tp-form-field">
                <label className="tp-field-label" htmlFor="series-seasons">Saisons</label>
                <input id="series-seasons" type="number" value={totalSeasons} onChange={e => setTotalSeasons(e.target.value)} min="1" className="tp-input" placeholder="—" />
              </div>
            )}
          </div>

          <div className="tp-form-panel">
            <div className="tp-form-grid">
              <div className="tp-form-field">
                <label className="tp-field-label" htmlFor="series-genre">Genre</label>
                <select id="series-genre" value={programGenreId} onChange={e => setProgramGenreId(e.target.value)} className="tp-select">
                  <option value="">— Aucun genre —</option>
                  {genreOptions.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                </select>
              </div>
              <div className="tp-form-field">
                <label className="tp-field-label" htmlFor="series-sub-genre">Sous-genre</label>
                {/* Champ libre, comme sur la fiche : les valeurs déjà saisies
                    sont proposées pour éviter les doublons de casse. */}
                <input
                  id="series-sub-genre"
                  value={subGenre}
                  onChange={e => setSubGenre(e.target.value)}
                  maxLength={100}
                  className="tp-input"
                  placeholder="Football, Économie…"
                  list="tp-series-sub-genres"
                />
                <datalist id="tp-series-sub-genres">
                  {subGenreOptions.map(s => <option key={s.label} value={s.label} />)}
                </datalist>
              </div>
            </div>
            <p className="catalog-empty-copy">
              Chaque nouvelle édition de la série reçoit ce genre et ce sous-genre, modifiables ensuite fiche par fiche.
              {!programGenreId && ' Sans genre, les éditions créées devront être classées une à une avant de partir à l’EPG.'}
            </p>
            {isEdit && unclassifiedCount > 0 && !!programGenreId && (
              <label className="tp-check-row">
                <input type="checkbox" checked={applyGenreToUnclassified} onChange={e => setApplyGenreToUnclassified(e.target.checked)} />
                Appliquer aussi aux {unclassifiedCount} édition{unclassifiedCount > 1 ? 's' : ''} déjà créée{unclassifiedCount > 1 ? 's' : ''} sans genre
              </label>
            )}
          </div>

          <div className="tp-form-panel">
            <label className="tp-check-row">
              <input type="checkbox" checked={numberEpisodes} onChange={e => setNumberEpisodes(e.target.checked)} />
              Numéroter les éditions (saison / épisode)
            </label>
            <p className="catalog-empty-copy">
              {numberEpisodes
                ? 'Chaque édition porte une saison et un numéro d’épisode, proposés à l’import et modifiables épisode par épisode.'
                : 'Les éditions ne portent ni saison ni épisode — le cas des émissions enregistrées en série et livrées en lot. Réactivable à tout moment.'}
              {isEdit && series?.numberEpisodes && !numberEpisodes && (
                <> <strong>Enregistrer effacera les numéros déjà attribués aux éditions de cette série.</strong></>
              )}
            </p>
          </div>

          <div className="tp-form-panel">
            <label className="tp-check-row">
              <input type="checkbox" checked={episodeSortDescending} onChange={e => setEpisodeSortDescending(e.target.checked)} />
              Trier les épisodes du plus récent au plus ancien
            </label>
            <p className="catalog-empty-copy">
              {episodeSortDescending
                ? 'Les éditions les plus récentes apparaissent en premier dans la fiche série.'
                : 'Les éditions apparaissent dans l’ordre chronologique, la plus ancienne en premier.'}
            </p>
          </div>

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="series-description">Description</label>
            <textarea id="series-description" value={description} onChange={e => setDescription(e.target.value)} rows={2} className="tp-textarea" placeholder="Résumé de la série…" />
          </div>

          <div className="tp-form-grid tp-form-grid--three">
            <SeriesField label="Réalisation"><input value={director} onChange={e => setDirector(e.target.value)} className="tp-input" /></SeriesField>
            <SeriesField label="Présentation"><input value={presenter} onChange={e => setPresenter(e.target.value)} className="tp-input" /></SeriesField>
            <SeriesField label="Production"><input value={production} onChange={e => setProduction(e.target.value)} className="tp-input" /></SeriesField>
          </div>

          <div className="tp-form-panel">
            <span className="tp-field-label">EPG — guide des programmes</span>
            <p className="catalog-empty-copy">
              Valeurs transmises à Plurimedia pour toutes les éditions de la série. Le genre publié est
              celui de chaque fiche programme. L’identifiant d’émission est commun à toutes les éditions :
              saisi ici, il prime sur celui d’une fiche. Ces identifiants sont attribués par le partenaire.
            </p>
            <div className="tp-form-grid tp-form-grid--four">
              <SeriesField label="Fédération">
                <input value={epgFederation} onChange={e => setEpgFederation(e.target.value)} className="tp-input" />
              </SeriesField>
              <SeriesField label="Identifiant de thème">
                <input type="number" min="0" value={epgThemeId} onChange={e => setEpgThemeId(e.target.value)} className="tp-input" placeholder="—" />
              </SeriesField>
              <SeriesField label="Identifiant d’émission">
                <input type="number" min="0" value={epgShowId} onChange={e => setEpgShowId(e.target.value)} className="tp-input" placeholder="—" />
              </SeriesField>
            </div>
          </div>

          <SeriesField label="Mots-clés (séparés par des virgules)">
            <input value={tags} onChange={e => setTags(e.target.value)} className="tp-input" />
          </SeriesField>

          <SeriesField label="Habillage logo">
            <select value={logoProfileId} onChange={e => setLogoProfileId(e.target.value)} disabled={!logoProfiles.length} required className="tp-select">
              {logoProfiles.filter(profile => profile.active).map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
            </select>
          </SeriesField>

          <label className="tp-check-row">
            <input type="checkbox" checked={isFourThree} onChange={e => setIsFourThree(e.target.checked)} />
            Format source 4/3
          </label>

          <div className="tp-form-panel">
            <div className="tp-form-grid">
              <SeriesField label="Nombre de parties">
                <input type="number" min="1" max="20" value={partDurationsMin.length} onChange={e => {
                  const count = Math.min(20, Math.max(1, Number(e.target.value)))
                  setPartDurationsMin(parts => Array.from({ length: count }, (_, index) => parts[index] ?? parts[0] ?? '52'))
                }} className="tp-input" />
              </SeriesField>
              <SeriesField label="Coupure entre parties (minutes)">
                <input type="number" min="0" step="0.5" value={interPartBreakMin} onChange={e => setInterPartBreakMin(e.target.value)} className="tp-input" />
              </SeriesField>
            </div>
            <div className="tp-form-grid tp-form-grid--four catalog-part-grid">
              {partDurationsMin.map((value, index) => (
                <SeriesField key={index} label={partDurationsMin.length === 1 && hasShortVersion ? 'Version longue (min)' : `Partie ${index + 1} (min)`}>
                  <input type="number" min="1" value={value} onChange={e => setPartDurationsMin(parts => parts.map((part, partIndex) => partIndex === index ? e.target.value : part))} className="tp-input" />
                </SeriesField>
              ))}
              {partDurationsMin.length === 1 && hasShortVersion && (
                <SeriesField label="Version courte (min)">
                  <input type="number" min="1" step="0.5" value={shortVersionMin} onChange={e => setShortVersionMin(e.target.value)} className="tp-input" />
                </SeriesField>
              )}
            </div>
            {/* Une série multipartie a déjà un fichier par partie : elle n'a pas
                de version courte. */}
            {partDurationsMin.length === 1 && (
              <>
                <label className="tp-check-row">
                  <input type="checkbox" checked={hasShortVersion} onChange={e => setHasShortVersion(e.target.checked)} />
                  Livrée en deux versions (longue et courte)
                </label>
                {hasShortVersion && (
                  <p className="catalog-empty-copy">
                    À la génération depuis un modèle, chaque créneau reçoit la version la plus proche de sa durée — la courte sur un créneau court, même avant la livraison de son fichier.
                    {!isEdit || !series?.shortVersionDurationSec ? ' Les éditions à venir déjà créées passent aussi en deux versions.' : ''}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !title.trim() || !logoProfileId} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})

function SeriesField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="tp-form-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
}
